/**
 * Call Centre API Service
 * Handles all API calls for the Call Centre module
 * Supports both regular JWT auth and agent session tokens
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// Get auth token - prefers agent token, falls back to regular JWT
const getAuthHeader = () => {
  // First check for agent token (Slack auth)
  const agentToken = localStorage.getItem('agentToken');
  if (agentToken) {
    return { Authorization: `Bearer ${agentToken}` };
  }

  // Fall back to regular JWT token
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: `${API_URL}/api/call-centre`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth header to all requests
api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeader() };
  return config;
});

// Handle 401 errors - redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear tokens and redirect to login
      localStorage.removeItem('agentToken');
      localStorage.removeItem('agentInfo');
      if (window.location.pathname.startsWith('/call-centre')) {
        window.location.href = '/call-centre/login';
      }
    }
    return Promise.reject(error);
  }
);

const callCentreService = {
  // ========== Agent Auth APIs ==========

  /**
   * Start Slack OAuth flow for agent login
   * @param {string} redirect - Optional redirect path after auth
   */
  startSlackAuth: async (redirect = '/call-centre') => {
    const response = await axios.get(`${API_URL}/api/call-centre/auth/slack/start?redirect=${encodeURIComponent(redirect)}`);
    return response.data;
  },

  /**
   * Complete Slack OAuth callback
   * @param {string} code - OAuth code from Slack
   * @param {string} state - OAuth state
   */
  completeSlackAuth: async (code, state) => {
    const response = await axios.post(`${API_URL}/api/call-centre/auth/slack/callback`, { code, state });
    return response.data;
  },

  /**
   * Verify agent session token
   */
  verifyAgentToken: async () => {
    const token = localStorage.getItem('agentToken');
    if (!token) {
      throw new Error('No agent token found');
    }

    const response = await axios.get(`${API_URL}/api/call-centre/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  /**
   * Logout agent
   */
  logout: async () => {
    const token = localStorage.getItem('agentToken');
    if (token) {
      try {
        await axios.post(`${API_URL}/api/call-centre/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // Ignore errors
      }
    }
    localStorage.removeItem('agentToken');
    localStorage.removeItem('agentInfo');
  },

  /**
   * Refresh agent session token silently
   * Returns new token + updated brand assignments
   */
  refreshAgentSession: async () => {
    const token = localStorage.getItem('agentToken');
    if (!token) return null;

    const response = await axios.post(`${API_URL}/api/call-centre/auth/refresh`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.data.success && response.data.token) {
      localStorage.setItem('agentToken', response.data.token);
      console.log('[CallCentre] Agent session refreshed silently');
    }

    return response.data;
  },

  // ========== Agent APIs ==========

  /**
   * Get current agent info
   * @param {string} slackUserId - Slack user ID
   */
  getAgentInfo: async (slackUserId) => {
    const response = await api.get(`/agents/me?slackUserId=${slackUserId}`);
    return response.data;
  },

  /**
   * Get SIP credentials for mobile app
   * @param {string} slackUserId - Slack user ID
   */
  getSipCredentials: async (slackUserId) => {
    const response = await api.get(`/agents/me/sip-credentials?slackUserId=${slackUserId}`);
    return response.data;
  },

  /**
   * Get SIP QR code for mobile app setup (Linphone)
   * Uses the agent's own endpoint - no admin access required
   */
  getSipQrCode: async () => {
    const response = await api.get('/agents/me/sip-qr?format=dataurl');
    return response.data;
  },

  /**
   * Get Twilio token for browser calling
   * @param {string} slackUserId - Slack user ID (optional - backend will lookup by email if not provided)
   */
  getTwilioToken: async (slackUserId) => {
    // Don't pass slackUserId if it's a placeholder - let backend lookup by email
    const shouldPassSlackId = slackUserId && slackUserId !== 'email_lookup' && !slackUserId.startsWith('user_');
    const query = shouldPassSlackId ? `?slackUserId=${slackUserId}` : '';
    const response = await api.get(`/agents/me/token${query}`);
    return response.data;
  },

  /**
   * Check agent availability
   * @param {string} slackUserId - Slack user ID
   */
  checkAvailability: async (slackUserId) => {
    const response = await api.get(`/agents/${slackUserId}/availability`);
    return response.data;
  },

  // ========== Presence Tracking APIs ==========

  /**
   * Send heartbeat to indicate agent is online
   * @param {string} slackUserId - Slack user ID
   * @param {string} deviceType - 'browser' or 'sip'
   */
  sendHeartbeat: async (slackUserId, deviceType = 'browser') => {
    const response = await api.post('/agents/heartbeat', { slackUserId, deviceType });
    return response.data;
  },

  /**
   * Mark agent as offline
   * @param {string} slackUserId - Slack user ID
   * @param {string} deviceType - 'browser' or 'sip'
   */
  markOffline: async (slackUserId, deviceType = 'browser') => {
    const response = await api.post('/agents/offline', { slackUserId, deviceType });
    return response.data;
  },

  /**
   * Get list of online agents
   * @param {number} brandId - Optional brand ID to filter by
   */
  getOnlineAgents: async (brandId) => {
    const query = brandId ? `?brandId=${brandId}` : '';
    const response = await api.get(`/agents/online${query}`);
    return response.data;
  },

  // ========== Customer APIs ==========

  /**
   * Lookup customer by phone number
   * @param {string} phone - Phone number
   * @param {number} brandId - Brand ID
   */
  lookupCustomer: async (phone, brandId) => {
    const response = await api.get(`/customers/lookup?phone=${encodeURIComponent(phone)}&brandId=${brandId}`);
    return response.data;
  },

  /**
   * Search customer by email
   * @param {string} email - Email address
   * @param {number} brandId - Brand ID
   */
  searchByEmail: async (email, brandId) => {
    const response = await api.get(`/customers/search/email?email=${encodeURIComponent(email)}&brandId=${brandId}`);
    return response.data;
  },

  /**
   * Search customer by order number
   * @param {string} orderNumber - Order number
   * @param {number} brandId - Brand ID
   */
  searchByOrder: async (orderNumber, brandId) => {
    const response = await api.get(`/customers/search/order?orderNumber=${encodeURIComponent(orderNumber)}&brandId=${brandId}`);
    return response.data;
  },

  /**
   * Search customer by name
   * @param {string} name - Customer name (first, last, or full name)
   * @param {number} brandId - Brand ID
   */
  searchByName: async (name, brandId) => {
    const response = await api.get(`/customers/search/name?name=${encodeURIComponent(name)}&brandId=${brandId}`);
    return response.data;
  },

  /**
   * Get customer's order history
   * @param {string} email - Customer email
   * @param {number} brandId - Brand ID
   */
  getCustomerOrders: async (email, brandId) => {
    const response = await api.get(`/customers/${encodeURIComponent(email)}/orders?brandId=${brandId}`);
    return response.data;
  },

  // ========== Call APIs ==========

  /**
   * Get call list
   * @param {Object} params - Query params (brandId, status, page, limit)
   */
  getCalls: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/calls?${query}`);
    return response.data;
  },

  /**
   * Get the playback URL for a call's recording.
   *
   * When the backend has RECORDINGS_PROXY_ENABLED=true this returns a short-
   * lived proxied URL (so the agent's browser never talks to api.twilio.com
   * directly). When disabled the backend returns the original Twilio URL,
   * giving us a transparent feature flag.
   *
   * Returns { url, has_recording, proxied } or throws.
   */
  getPlaybackUrl: async (callId) => {
    const response = await api.post(`/calls/${callId}/playback-url`);
    return response.data;
  },

  /**
   * Get call stats
   * @param {Object} params - Query params (brandId, period)
   */
  getCallStats: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/calls/stats?${query}`);
    return response.data;
  },

  /**
   * Get active calls
   */
  getActiveCalls: async () => {
    const response = await api.get('/calls/active');
    return response.data;
  },

  /**
   * Get single call details
   * @param {number} callId - Call ID
   */
  getCall: async (callId) => {
    const response = await api.get(`/calls/${callId}`);
    return response.data;
  },

  /**
   * Update call notes
   * @param {number} callId - Call ID
   * @param {string} notes - Notes text
   */
  updateCallNotes: async (callId, notes) => {
    const response = await api.put(`/calls/${callId}/notes`, { notes });
    return response.data;
  },

  /**
   * Get pending calls (missed, not called back) sorted by lifetime value
   * @param {Object} params - Query params (brandId, limit)
   */
  getPendingCalls: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/calls/missed/need-callback?${query}`);
    return response.data;
  },

  resolvePendingCall: async (callId) => {
    const response = await api.put(`/calls/${callId}/resolve`);
    return response.data;
  },

  /**
   * Get calls for a customer phone
   * @param {string} phone - Customer phone
   * @param {number} brandId - Brand ID
   */
  getCustomerCalls: async (phone, brandId) => {
    const response = await api.get(`/calls/customer/${encodeURIComponent(phone)}?brandId=${brandId}`);
    return response.data;
  },

  // ========== Call Notes & Tags APIs ==========

  /**
   * Add a note to a call
   * @param {number} callId - Call ID
   * @param {string} noteText - Note text
   * @param {string} noteType - Note type (default: 'general')
   */
  addCallNote: async (callId, noteText, noteType = 'general') => {
    const response = await api.post(`/calls/${callId}/notes`, { noteText, noteType });
    return response.data;
  },

  deleteCallNote: async (callId, noteId) => {
    const response = await api.delete(`/calls/${callId}/notes/${noteId}`);
    return response.data;
  },

  /**
   * Get tags for a call
   * @param {number} callId - Call ID
   */
  getCallTags: async (callId) => {
    const response = await api.get(`/calls/${callId}`);
    return response.data;
  },

  /**
   * Add a tag to a call
   * @param {number} callId - Call ID
   * @param {string} tag - Tag text
   * @param {string} slackUserId - Agent Slack user ID (optional)
   * @param {string} agentName - Agent name (optional)
   */
  addCallTag: async (callId, tag, slackUserId, agentName) => {
    const response = await api.post(`/calls/${callId}/tags`, { tag, slackUserId, agentName });
    return response.data;
  },

  /**
   * Remove a tag from a call
   * @param {number} callId - Call ID
   * @param {number} tagId - Tag ID
   */
  removeCallTag: async (callId, tagId) => {
    const response = await api.delete(`/calls/${callId}/tags/${tagId}`);
    return response.data;
  },

  /**
   * Get popular tags for autocomplete
   * @param {number} brandId - Brand ID
   * @param {string} query - Search prefix
   */
  getPopularTags: async (brandId, query) => {
    const params = {};
    if (brandId) params.brandId = brandId;
    if (query) params.q = query;
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/calls/tags/popular?${qs}`);
    return response.data;
  },

  // ========== Voicemail APIs ==========

  /**
   * Get voicemails
   * @param {Object} params - Query params (brandId, status, page, limit)
   */
  getVoicemails: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/voicemails?${query}`);
    return response.data;
  },

  /**
   * Get new voicemails count
   */
  getNewVoicemails: async () => {
    const response = await api.get('/voicemails/new');
    return response.data;
  },

  /**
   * Mark voicemail as listened
   * @param {number} voicemailId - Voicemail ID
   */
  markVoicemailListened: async (voicemailId) => {
    const response = await api.put(`/voicemails/${voicemailId}/listened`);
    return response.data;
  },

  /**
   * Action a voicemail
   * @param {number} voicemailId - Voicemail ID
   * @param {Object} data - Action data (action, notes)
   */
  actionVoicemail: async (voicemailId, data) => {
    const response = await api.put(`/voicemails/${voicemailId}/action`, data);
    return response.data;
  },

  // ========== Callback APIs ==========

  /**
   * Get callbacks
   * @param {Object} params - Query params (brandId, status, page, limit)
   */
  getCallbacks: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/callbacks?${query}`);
    return response.data;
  },

  /**
   * Claim a callback
   * @param {number} callbackId - Callback ID
   * @param {string} slackUserId - Agent's Slack user ID
   */
  claimCallback: async (callbackId, slackUserId) => {
    const response = await api.put(`/callbacks/${callbackId}/claim`, { slackUserId });
    return response.data;
  },

  /**
   * Complete a callback
   * @param {number} callbackId - Callback ID
   * @param {Object} data - Completion data (callId, notes)
   */
  completeCallback: async (callbackId, data) => {
    const response = await api.put(`/callbacks/${callbackId}/complete`, data);
    return response.data;
  },

  // ========== Order APIs ==========

  /**
   * Search products
   * @param {number} brandId - Brand ID
   * @param {string} query - Search query
   */
  searchProducts: async (brandId, query) => {
    const response = await api.get(`/orders/products/search?brandId=${brandId}&query=${encodeURIComponent(query)}`);
    return response.data;
  },

  /**
   * Create draft order
   * @param {Object} orderData - Order data
   */
  createDraftOrder: async (orderData) => {
    const response = await api.post('/orders/draft', orderData);
    return response.data;
  },

  /**
   * Send draft order via SMS
   * @param {number} draftOrderId - Draft order ID
   */
  sendDraftOrderSms: async (draftOrderId) => {
    const response = await api.post(`/orders/draft/${draftOrderId}/send-sms`);
    return response.data;
  },

  /**
   * Send draft order via email
   * @param {number} draftOrderId - Draft order ID
   */
  sendDraftOrderEmail: async (draftOrderId) => {
    const response = await api.post(`/orders/draft/${draftOrderId}/send-email`);
    return response.data;
  },

  /**
   * Get draft orders
   * @param {Object} params - Query params (brandId, status, page, limit)
   */
  getDraftOrders: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/orders/drafts?${query}`);
    return response.data;
  },

  // ========== Admin APIs ==========

  /**
   * Get admin dashboard stats
   */
  getAdminDashboard: async () => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  /**
   * Get phone numbers
   * @param {number} brandId - Optional brand ID filter
   */
  getPhoneNumbers: async (brandId) => {
    const query = brandId ? `?brandId=${brandId}` : '';
    const response = await api.get(`/agents/phone-numbers${query}`);
    return response.data;
  },

  /**
   * Add phone number
   * @param {Object} data - Phone number data
   */
  addPhoneNumber: async (data) => {
    const response = await api.post('/admin/phone-numbers', data);
    return response.data;
  },

  /**
   * Update phone number
   * @param {number} id - Phone number ID
   * @param {Object} data - Update data
   */
  updatePhoneNumber: async (id, data) => {
    const response = await api.put(`/admin/phone-numbers/${id}`, data);
    return response.data;
  },

  /**
   * Delete phone number (soft delete)
   * @param {number} id - Phone number ID
   */
  deletePhoneNumber: async (id) => {
    const response = await api.delete(`/admin/phone-numbers/${id}`);
    return response.data;
  },

  /**
   * Get Twilio phone numbers directly from Twilio account
   */
  getTwilioNumbers: async () => {
    const response = await api.get('/admin/twilio-numbers');
    return response.data;
  },

  /**
   * Configure Twilio phone number webhook for call centre
   * @param {Object} data - { phoneNumberSid, phoneNumber }
   */
  configureTwilioNumber: async (data) => {
    const response = await api.post('/admin/twilio-configure-number', data);
    return response.data;
  },

  /**
   * Get Twilio setup status
   */
  getTwilioStatus: async () => {
    const response = await api.get('/admin/twilio-status');
    return response.data;
  },

  /**
   * Setup Twilio for browser calling (create API key and TwiML app)
   * @param {Object} data - { webhookBaseUrl }
   */
  setupTwilio: async (data = {}) => {
    const response = await api.post('/admin/twilio-setup', data);
    return response.data;
  },

  /**
   * Get agent-brand assignments
   * @param {Object} params - Query params (brandId, slackUserId)
   */
  getAgentBrands: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/agent-brands?${query}`);
    return response.data;
  },

  /**
   * Assign agent to brand
   * @param {Object} data - Assignment data
   */
  assignAgentToBrand: async (data) => {
    const response = await api.post('/admin/agent-brands', data);
    return response.data;
  },

  /**
   * Update agent-brand assignment
   * @param {number} assignmentId - Assignment ID
   * @param {Object} data - Update data (priority, isActive, etc.)
   */
  updateAgentBrand: async (assignmentId, data) => {
    const response = await api.put(`/admin/agent-brands/${assignmentId}`, data);
    return response.data;
  },

  /**
   * Remove agent-brand assignment
   * @param {number} assignmentId - Assignment ID
   */
  removeAgentBrand: async (assignmentId) => {
    const response = await api.delete(`/admin/agent-brands/${assignmentId}`);
    return response.data;
  },

  /**
   * Get Slack users for agent selection
   */
  getSlackUsers: async () => {
    const response = await api.get('/admin/slack-users');
    return response.data;
  },

  /**
   * Get all brands for the company
   */
  getCompanyBrands: async () => {
    const response = await api.get('/admin/brands');
    return response.data;
  },

  // ========== Push Notification APIs ==========

  /**
   * Get VAPID public key for push notifications
   */
  getVapidPublicKey: async () => {
    const response = await api.get('/push/vapid-key');
    return response.data;
  },

  /**
   * Save push subscription
   * @param {Object} data - { subscription, slackUserId }
   */
  savePushSubscription: async (data) => {
    const response = await api.post('/push/subscribe', data);
    return response.data;
  }
};

export default callCentreService;
